import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as OkIcon,
  ErrorOutline as NotOkIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from '@/api/client';
import { useCreateMirrorChannel, useMirrorOnboarding } from '@/hooks/useMirror';

interface MirrorCreateDialogProps {
  open: boolean;
  chatId: number;
  chatName: string | null;
  onClose: () => void;
}

/**
 * Onboard one channel onto the mirror. The backend writes three rows in ONE
 * transaction (backup_channels + backup_archive_routes + backup_channel_posters);
 * this dialog's whole job is to make the two things it cannot enforce explicit:
 *
 * - **Membership (D5).** The poster must already be a proven member of the
 *   source — `scraper_account_chats.verified_at`. Accounts that are not are
 *   listed but disabled, with the reason, rather than hidden: "why can't I pick
 *   igram" is a question the UI should answer, not swallow. Joining happens in
 *   the chat-preview join dialog; nothing here spends join budget.
 * - **The archive precondition.** SQL cannot see whether the archive is a forum
 *   or whether the bot is an admin there with Manage Topics, so the operator
 *   confirms it. Unchecked is a "no": the server refuses without the flag.
 *
 * The archive itself is fixed (decision 2026-08-13-archive-picker.md) and shown
 * read-only. `mirror_mode`, poster `weight` and per-channel rate overrides are
 * deliberately absent — no runtime reads them.
 */
const MirrorCreateDialog: React.FC<MirrorCreateDialogProps> = ({
  open,
  chatId,
  chatName,
  onClose,
}) => {
  const { t } = useTranslation('pages');
  const { data: onboarding, isLoading, isError } = useMirrorOnboarding(open);
  const createMutation = useCreateMirrorChannel();

  const [accountId, setAccountId] = useState<number | null>(null);
  const [backupChannel, setBackupChannel] = useState<string>('');
  const [ack, setAck] = useState(false);
  const [error, setError] = useState('');

  const accounts = onboarding?.accounts ?? [];
  const memberships = onboarding?.memberships ?? [];
  const preconditions = onboarding?.preconditions;

  const isMember = (id: number) =>
    memberships.some((m) => m.account_id === id && m.chats_id === chatId);

  // Derived, not stored in an effect: the account list and the suggestion both
  // arrive after the first paint, so state-only defaults would render empty for
  // that frame. An explicit pick wins while it is still a member.
  const defaultAccountId = accounts.find((a) => isMember(a.id))?.id ?? null;
  const selectedAccountId =
    accountId != null && accounts.some((a) => a.id === accountId) ? accountId : defaultAccountId;
  const effectiveBackupChannel =
    backupChannel !== '' ? backupChannel : onboarding?.suggested_backup_channel ?? '';

  const canSubmit =
    !!selectedAccountId &&
    isMember(selectedAccountId) &&
    effectiveBackupChannel.trim() !== '' &&
    ack &&
    !createMutation.isPending;

  const handleClose = () => {
    setError('');
    createMutation.reset();
    onClose();
  };

  const handleCreate = async () => {
    setError('');
    if (!selectedAccountId) return;
    try {
      await createMutation.mutateAsync({
        source_chats_id: chatId,
        backup_channel: effectiveBackupChannel.trim(),
        account_id: selectedAccountId,
        archive_precondition_ack: ack,
      });
      handleClose();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  /** A checklist line. `null` reads as "not satisfied", never as "probably on". */
  const SwitchLine: React.FC<{ label: string; value: boolean | null }> = ({ label, value }) => (
    <Box display="flex" alignItems="center" gap={1}>
      {value ? (
        <OkIcon fontSize="small" color="success" />
      ) : (
        <NotOkIcon fontSize="small" color="warning" />
      )}
      <Typography variant="body2">{label}</Typography>
      <Typography variant="caption" color="text.secondary" dir="ltr">
        {value === null ? t('mirror.unknown') : value ? '1' : '0'}
      </Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('mirror.create.title')}</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}

        {isError && <Alert severity="error">{t('mirror.create.loadFailed')}</Alert>}

        {!isLoading && !isError && (
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box>
              <Typography variant="subtitle2">{t('mirror.create.source')}</Typography>
              <Typography variant="body2">{chatName || `#${chatId}`}</Typography>
              <Typography variant="caption" color="text.secondary" dir="ltr">
                chats.id {chatId}
              </Typography>
            </Box>

            <Divider />

            {accounts.length === 0 ? (
              <Alert severity="warning">{t('mirror.create.noPosterAccounts')}</Alert>
            ) : (
              <TextField
                select
                fullWidth
                size="small"
                label={t('mirror.create.posterAccount')}
                value={selectedAccountId ?? ''}
                onChange={(e) => setAccountId(Number(e.target.value))}
                helperText={t('mirror.create.posterHelp')}
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id} disabled={!isMember(account.id)}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span dir="ltr">{account.label}</span>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={isMember(account.id) ? 'success' : 'default'}
                        label={
                          isMember(account.id)
                            ? t('mirror.create.member')
                            : t('mirror.create.notMember')
                        }
                      />
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            )}

            {selectedAccountId != null && !isMember(selectedAccountId) && (
              <Alert severity="warning">{t('mirror.create.membershipRequired')}</Alert>
            )}

            <TextField
              fullWidth
              size="small"
              label={t('mirror.create.intermediary')}
              value={effectiveBackupChannel}
              onChange={(e) => setBackupChannel(e.target.value)}
              inputProps={{ dir: 'ltr' }}
              helperText={t('mirror.create.intermediaryHelp')}
            />

            <Box>
              <Typography variant="subtitle2">{t('mirror.create.archive')}</Typography>
              <Typography variant="body2" dir="ltr">
                {preconditions?.archive_chat_id ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('mirror.create.archiveHelp')}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('mirror.create.checklist')}
              </Typography>
              <SwitchLine
                label={t('mirror.create.switchMirrorSend')}
                value={preconditions?.mirror_send_enabled ?? null}
              />
              <SwitchLine
                label={t('mirror.create.switchMirrorEnabled')}
                value={preconditions?.mirror_enabled ?? null}
              />
              <Typography variant="caption" color="text.secondary">
                {t('mirror.create.switchHelp')}
              </Typography>
              <FormControlLabel
                sx={{ mt: 1, alignItems: 'flex-start' }}
                control={
                  <Checkbox checked={ack} onChange={(e) => setAck(e.target.checked)} size="small" />
                }
                label={
                  <Typography variant="body2">{t('mirror.create.archiveAck')}</Typography>
                }
              />
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('mirror.create.cancel')}</Button>
        <Button variant="contained" onClick={handleCreate} disabled={!canSubmit}>
          {createMutation.isPending ? (
            <CircularProgress size={18} />
          ) : (
            t('mirror.create.submit')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MirrorCreateDialog;
